/**
 * Translate old rules model to new display rule format
 *
 * Previous Rules format:
 * {
 *   'dayswithcnm.com/gifts': {
 *     openDelay: 10000,
 *     displayDelay: 5000,
 *     type: 'CONTAINS',
 *     message: ruleMessage,
 *   },
 * }
 *
 * DisplayRule format:
 * {
 *   showByDefault: false,
 *   rules: [
 *     {
 *       type: "URL",
 *       id: "some-id",
 *       displayDelay: 100,
 *       content: {
 *         matchType: "CONTAINS",
 *         value: "dayswithcnm.com/gifts"
 *       }
 *     },
 *   ],
 * }
 */

import type { LegacyRule } from '@/configuration/translateRules';
import { DISPLAY_TYPE, type DisplayType } from '@/domain/configuration';
import generateId from '@/lib/generateId';

export interface DisplayRule {
  type: string;
  id: string;
  displayDelay: number;
  content: {
    matchType: string;
    value: string;
  };
}

export interface DisplayRules {
  showByDefault: boolean;
  rules: DisplayRule[];
}

function isLegacyRule(value: unknown): value is LegacyRule {
  return typeof value === 'object' && value !== null;
}

export default function translateToDisplayRules(
  displayType: DisplayType | string | undefined,
  rules: Record<string, unknown> | Record<string, LegacyRule> | undefined | null,
): DisplayRules {
  const displayRules: DisplayRules = {
    showByDefault: shouldShowByDefault(displayType),
    rules: [],
  };

  if (displayType !== DISPLAY_TYPE.RULES_ONLY || !rules) {
    return displayRules;
  }

  for (const key in rules) {
    const rule = rules[key];
    if (isLegacyRule(rule)) {
      displayRules.rules.push(translateRule(key, rule));
    }
  }

  return displayRules;
}

function translateRule(key: string, rule: LegacyRule): DisplayRule {
  return {
    type: 'URL',
    id: generateId(),
    displayDelay: rule.displayDelay ?? 0,
    content: {
      matchType: rule.type ?? 'CONTAINS',
      value: key,
    },
  };
}

function shouldShowByDefault(displayType: DisplayType | string | undefined): boolean {
  switch (displayType) {
    case DISPLAY_TYPE.ALL_PAGES:
      return true;
    case DISPLAY_TYPE.RULES_ONLY:
      return false;
    case DISPLAY_TYPE.DISABLED:
      return false;
    default:
      return true;
  }
}
